import { parseCheckoutEntitlementSnapshotPayload } from "./checkout-entitlement-snapshot";
import type { CheckoutEntitlementSnapshot } from "./checkout-entitlement-snapshot";
import type { EntitlementState } from "./paid-features";
import { CURRENT_ENTITLEMENT_STATE } from "./paid-features";
import { isSafePurchaseInstallId } from "./purchase-installation-id";

const OWNED_PURCHASE_VERIFICATION_ENDPOINT =
  "https://polenest-entitlements.itseoulite.workers.dev/v1/invoice-web/entitlements/restore";

export interface PurchaseVerificationConfig {
  endpoint: string;
  metadataPolicy: "no_document_contents";
}

export type PurchaseVerificationConfigResult =
  | { type: "configured"; config: PurchaseVerificationConfig }
  | {
      type: "reject";
      reason:
        | "not_configured"
        | "invalid_shape"
        | "unsafe_endpoint"
        | "unsafe_public_value"
        | "metadata_policy_not_safe";
    };

export type PurchaseVerificationResult =
  | {
      ok: true;
      state: EntitlementState;
      hasActiveEntitlement: boolean;
      installId: string;
      snapshot: CheckoutEntitlementSnapshot;
    }
  | {
      ok: false;
      state: EntitlementState;
      reason:
        | "not_configured"
        | "invalid_install_id"
        | "active_browser_limit_reached"
        | "paypal_purchase_revoked"
        | "request_failed"
        | "invalid_response"
        | "inactive_entitlement";
    };

export function parsePurchaseVerificationConfig(input: unknown): PurchaseVerificationConfigResult {
  if (input == null || input === "") return { type: "reject", reason: "not_configured" };

  const raw =
    typeof input === "string" ? { endpoint: input, metadataPolicy: "no_document_contents" } : input;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { type: "reject", reason: "invalid_shape" };
  }

  const candidate = raw as Partial<PurchaseVerificationConfig>;
  if (typeof candidate.endpoint !== "string" || typeof candidate.metadataPolicy !== "string") {
    return { type: "reject", reason: "invalid_shape" };
  }
  if (containsUnsafePublicValue(candidate.endpoint)) {
    return { type: "reject", reason: "unsafe_public_value" };
  }
  if (!isSafeVerificationEndpoint(candidate.endpoint)) {
    return { type: "reject", reason: "unsafe_endpoint" };
  }
  if (candidate.metadataPolicy !== "no_document_contents") {
    return { type: "reject", reason: "metadata_policy_not_safe" };
  }

  return {
    type: "configured",
    config: {
      endpoint: candidate.endpoint,
      metadataPolicy: "no_document_contents",
    },
  };
}

export async function restorePurchaseVerification({
  config,
  installId,
  fetcher = fetch,
  now = new Date().toISOString(),
}: {
  config: PurchaseVerificationConfigResult;
  installId: string | null;
  fetcher?: typeof fetch;
  now?: string;
}): Promise<PurchaseVerificationResult> {
  if (config.type !== "configured") {
    return { ok: false, state: CURRENT_ENTITLEMENT_STATE, reason: "not_configured" };
  }
  if (!installId || !isSafePurchaseInstallId(installId)) {
    return { ok: false, state: CURRENT_ENTITLEMENT_STATE, reason: "invalid_install_id" };
  }

  let response: Response;
  try {
    response = await fetcher(config.config.endpoint, {
      method: "POST",
      credentials: config.config.endpoint.startsWith("/") ? "same-origin" : "omit",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        installId,
        metadataPolicy: config.config.metadataPolicy,
      }),
    });
  } catch {
    return { ok: false, state: CURRENT_ENTITLEMENT_STATE, reason: "request_failed" };
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const errorCode = safePurchaseRestoreErrorCode(payload);
    return {
      ok: false,
      state: CURRENT_ENTITLEMENT_STATE,
      reason: errorCode ?? "request_failed",
    };
  }

  const payload = await response.json().catch(() => null);
  const parsed = parseCheckoutEntitlementSnapshotPayload(payload, now);
  if (!parsed.ok) {
    return { ok: false, state: CURRENT_ENTITLEMENT_STATE, reason: "invalid_response" };
  }
  if (!parsed.hasActiveEntitlement) {
    return { ok: false, state: CURRENT_ENTITLEMENT_STATE, reason: "inactive_entitlement" };
  }
  return {
    ok: true,
    state: parsed.state,
    hasActiveEntitlement: true,
    installId,
    snapshot: parsed.snapshot,
  };
}

function safePurchaseRestoreErrorCode(
  payload: unknown,
): "active_browser_limit_reached" | "paypal_purchase_revoked" | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const error = (payload as { error?: unknown }).error;
  if (!error || typeof error !== "object" || Array.isArray(error)) return null;
  const code = (error as { code?: unknown }).code;
  return code === "active_browser_limit_reached" || code === "paypal_purchase_revoked"
    ? code
    : null;
}

function isSafeVerificationEndpoint(value: string): boolean {
  if (value === "/api/purchase-restore") return true;
  if (value === OWNED_PURCHASE_VERIFICATION_ENDPOINT) return true;
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.origin === "https://polenest-entitlements.itseoulite.workers.dev" &&
      url.pathname === "/v1/invoice-web/entitlements/restore" &&
      url.search === "" &&
      url.hash === "" &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}

function containsUnsafePublicValue(value: string): boolean {
  const lower = value.toLowerCase();
  return (
    /sk_(test|live)_/i.test(value) ||
    /whsec_/i.test(value) ||
    lower.includes("checkout.stripe.com") ||
    lower.includes("localstorage") ||
    lower.includes("indexeddb") ||
    lower.includes("invoicecontents") ||
    lower.includes("documentcontents")
  );
}
